// frontend/src/pages/admin/SecuriteAdmin.jsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './SecuriteAdmin.css';

const statusLabel = { active: 'Actif', suspended: 'Suspendu', inactive: 'Inactif' };
const statusClass  = { active: 'badge-active', suspended: 'badge-suspended', inactive: 'badge-inactive' };

const ROLE_LABELS = {
  super_admin:     'Super Admin',
  admin_financier: 'Admin Financier',
  support:         'Support',
  analyste:        'Analyste',
};

const MODULES = ['Packs B2B', 'Transactions', 'Devises', 'Suspension partenaire', 'Gestion admins', 'Remboursement'];
const ROLES_EDITABLE = ['admin_financier', 'support', 'analyste'];

// Matrice par défaut (true = accès activé, false = accès désactivé)
const DEFAULT_PERM_MATRIX = {
  admin_financier: { 'Packs B2B': true,  'Transactions': true,  'Devises': false, 'Suspension partenaire': true,  'Gestion admins': false, 'Remboursement': true  },
  support:         { 'Packs B2B': false, 'Transactions': true,  'Devises': false, 'Suspension partenaire': false, 'Gestion admins': false, 'Remboursement': false },
  analyste:        { 'Packs B2B': true,  'Transactions': true,  'Devises': true,  'Suspension partenaire': false, 'Gestion admins': false, 'Remboursement': false },
};

// Maintenu pour la prévisualisation dans les modales (legacy)
const PERMISSIONS = {
  admin_financier: { 'Packs B2B': '✔ Complet', 'Transactions': '✔ Complet', 'Devises': '❌', 'Suspension partenaire': '✔ Complet', 'Gestion admins': '❌', 'Remboursement': '✔ Complet' },
  support:         { 'Packs B2B': '❌', 'Transactions': '📊 Lecture', 'Devises': '❌', 'Suspension partenaire': '❌', 'Gestion admins': '❌', 'Remboursement': '❌' },
  analyste:        { 'Packs B2B': '📊 Lecture', 'Transactions': '📊 Lecture', 'Devises': '📊 Lecture', 'Suspension partenaire': '❌', 'Gestion admins': '❌', 'Remboursement': '❌' },
};

const PERM_CLASS = { '✔ Complet': 'sa-perm-yes', '❌': 'sa-perm-no', '📊 Lecture': 'sa-perm-readonly' };

const LOG_LABELS = {
  update_commission:       'Modification commission',
  update_currency_rate:    'Modification taux',
  update_pack:             'Modification pack',
  approve_partner:         'Approbation partenaire',
  suspend_partner:         'Suspension partenaire',
  suspend_client:          'Suspension client',
  reactivate_partner:      'Réactivation partenaire',
  update_admin_role:       'Modification rôle',
  create_pack:             'Création pack',
  refund:                  'Remboursement',
  login_success:           'Connexion réussie',
  login_failed:            'Échec connexion',
  force_password_reset:    'Réinitialisation MDP',
  create_admin:            'Création admin',
  suspend_admin:           'Suspension admin',
  reactivate_admin:        'Réactivation admin',
  update_security_settings:'Modif. paramètres sécu',
  reject_partner:          'Refus partenaire',
};

const CRITICAL_ACTIONS = ['update_commission','update_currency_rate','update_pack','approve_partner','suspend_partner','suspend_client','suspend_admin','refund'];
const WARNING_ACTIONS  = ['update_admin_role','reject_partner','force_password_reset','create_admin','update_security_settings'];

const getLogLevel = (action) => {
  if (CRITICAL_ACTIONS.includes(action)) return 'critical';
  if (WARNING_ACTIONS.includes(action))  return 'warning';
  return 'normal';
};

const LOG_LEVEL_LABELS = { critical: 'Critique', warning: 'Avertissement', normal: 'Normal' };

const EMPTY_FORM = { email: '', password: '', confirmPassword: '', role: 'admin_financier' };

// Calcule l'info d'expiration d'un rôle
const getExpiryInfo = (role_expires_at, role) => {
  if (role === 'super_admin' || !role_expires_at) return { type: 'permanent', label: 'Permanent', daysLeft: null };
  const expires = new Date(role_expires_at);
  const now     = new Date();
  if (expires < now) return { type: 'expired', label: 'Expiré', daysLeft: 0 };
  const daysLeft = Math.ceil((expires - now) / 86400000);
  if (daysLeft <= 3)  return { type: 'critical', label: `${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`, daysLeft };
  if (daysLeft <= 7)  return { type: 'soon',     label: `${daysLeft}j restants`, daysLeft };
  return { type: 'limited', label: `Expire le ${expires.toLocaleDateString('fr')}`, daysLeft };
};

// Min datetime-local = maintenant + 1h
const minDatetime = () => {
  const d = new Date(Date.now() + 3600000);
  return d.toISOString().slice(0, 16);
};

const SecuriteAdmin = () => {
  const [admins,         setAdmins]         = useState([]);
  const [auditLogs,      setAuditLogs]      = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [modal,          setModal]          = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [currentPage,    setCurrentPage]    = useState(1);
  const itemsPerPage = 10;

  // Edit admin
  const [editRole,        setEditRole]        = useState('admin_financier');
  const [editStatus,      setEditStatus]      = useState('active');
  const [force2FA,        setForce2FA]        = useState(false);
  const [editIsPermanent, setEditIsPermanent] = useState(true);
  const [editExpiresAt,   setEditExpiresAt]   = useState('');
  const [editError,       setEditError]       = useState('');

  // Nouvel admin
  const [newAdminForm, setNewAdminForm] = useState(EMPTY_FORM);
  const [createError,  setCreateError]  = useState('');

  // Audit logs
  const [logFilters,    setLogFilters]    = useState({ admin_id: 'all', action: 'all', level: 'all', date_from: '', date_to: '', search: '' });
  const [expandedLogId, setExpandedLogId] = useState(null);

  // Matrice permissions toggleable
  const [permMatrix, setPermMatrix] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_perm_matrix');
      return saved ? JSON.parse(saved) : DEFAULT_PERM_MATRIX;
    } catch { return DEFAULT_PERM_MATRIX; }
  });
  const [permSaved, setPermSaved] = useState(false);

  const togglePerm = (role, module) => {
    setPermMatrix(prev => {
      const updated = {
        ...prev,
        [role]: { ...prev[role], [module]: !prev[role][module] },
      };
      return updated;
    });
    setPermSaved(false);
  };

  const savePermMatrix = () => {
    localStorage.setItem('admin_perm_matrix', JSON.stringify(permMatrix));
    setPermSaved(true);
    setTimeout(() => setPermSaved(false), 2000);
  };

  const resetPermMatrix = () => {
    setPermMatrix(DEFAULT_PERM_MATRIX);
    localStorage.removeItem('admin_perm_matrix');
    setPermSaved(false);
  };

  // Settings
  const [settings, setSettings] = useState({
    twofa_required: true, session_duration: 30, max_attempts: 5,
    password_complexity: true, password_expiry: 90,
    double_refund: true, double_commission: true, ip_restriction: false,
  });

  // ── Alertes ──────────────────────────────────────────────
  const generateAlerts = useCallback((adminsList, logsList) => {
    const alerts = [];
    const failedLogins = logsList.filter(l => l.action === 'login_failed' || l.action === 'failed_login');
    if (failedLogins.length > 0) {
      alerts.push({ type: 'red', icon: 'fa-exclamation-triangle',
        title: `${failedLogins.length} tentative(s) suspecte(s)`, desc: 'Échecs de connexion récents' });
    }
    const criticals = logsList.filter(l => CRITICAL_ACTIONS.includes(l.action));
    if (criticals.length > 0 && criticals[0].created_at) {
      const min = Math.floor((Date.now() - new Date(criticals[0].created_at)) / 60000);
      alerts.push({ type: 'yellow', icon: 'fa-clock',
        title: 'Action critique récente', desc: `${LOG_LABELS[criticals[0].action] || criticals[0].action} il y a ${min} min` });
    }
    const no2FA = adminsList.filter(a => !a.two_factor_enabled);
    if (no2FA.length > 0) {
      alerts.push({ type: 'blue', icon: 'fa-shield-alt',
        title: '2FA obligatoire', desc: `${no2FA.length} admin(s) sans 2FA active` });
    }
    // Rôles qui expirent bientôt
    const expiringSoon = adminsList.filter(a => {
      if (!a.role_expires_at) return false;
      const d = Math.ceil((new Date(a.role_expires_at) - new Date()) / 86400000);
      return d > 0 && d <= 3;
    });
    if (expiringSoon.length > 0) {
      alerts.push({ type: 'yellow', icon: 'fa-hourglass-half',
        title: 'Accès expirant bientôt',
        desc: `${expiringSoon.length} admin(s) : accès expire dans moins de 3 jours` });
    }
    setSecurityAlerts(alerts);
  }, []);

  // ── Chargement ────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [adminsRes, logsRes, settingsRes] = await Promise.all([
        API.get('/admin/security/admins'),
        API.get('/admin/security/audit-logs', { params: { limit: 500, offset: 0 } }),
        API.get('/admin/security/settings').catch(() => ({ data: { settings: {} } })),
      ]);
      setAdmins(adminsRes.data.admins || []);
      setAuditLogs(logsRes.data.logs || []);
      if (settingsRes.data.settings) setSettings(p => ({ ...p, ...settingsRes.data.settings }));
      generateAlerts(adminsRes.data.admins || [], logsRes.data.logs || []);
    } catch (e) {
      console.error('Erreur chargement données:', e);
    } finally {
      setLoading(false);
    }
  }, [generateAlerts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Handlers admin ────────────────────────────────────────
  const handleSaveAdmin = async () => {
    setEditError('');
    if (!editIsPermanent && !editExpiresAt) {
      setEditError('Veuillez choisir une date d\'expiration.');
      return;
    }
    setSaving(true);
    try {
      await API.put(`/admin/security/admins/${modal.admin.id}`, {
        role:            editRole,
        status:          editStatus,
        force_2fa:       force2FA,
        role_expires_at: editIsPermanent ? null : editExpiresAt,
      });
      await fetchData();
      setModal(null);
    } catch (e) {
      setEditError(e.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspendAdmin = async (adminId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir suspendre cet administrateur ?')) return;
    try {
      await API.put(`/admin/security/admins/${adminId}/suspend`);
      await fetchData();
    } catch (e) { console.error(e); }
  };

  const handleReactivateAdmin = async (adminId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir réactiver cet administrateur ?')) return;
    try {
      await API.put(`/admin/security/admins/${adminId}/reactivate`);
      await fetchData();
    } catch (e) { console.error(e); }
  };

  const handleCreateAdmin = async () => {
    setCreateError('');
    const { email, password, confirmPassword, role } = newAdminForm;
    if (!email || !password || !role)        { setCreateError('Tous les champs sont requis.'); return; }
    if (password !== confirmPassword)         { setCreateError('Les mots de passe ne correspondent pas.'); return; }
    if (password.length < 8)                  { setCreateError('Minimum 8 caractères.'); return; }
    setSaving(true);
    try {
      await API.post('/admin/security/admins', { email, password, role });
      await fetchData();
      setModal(null);
      setNewAdminForm(EMPTY_FORM);
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Erreur lors de la création.');
    } finally {
      setSaving(false);
    }
  };

  // ── Settings ──────────────────────────────────────────────
  const toggleSetting = (key) => setSettings(p => ({ ...p, [key]: !p[key] }));
  const saveSettings = async () => {
    try {
      await API.put('/admin/security/settings', settings);
      alert('Paramètres sauvegardés');
    } catch (e) { console.error(e); }
  };

  // ── Audit logs filtrés ────────────────────────────────────
  const filteredLogs = useMemo(() => {
    return auditLogs.filter(log => {
      if (logFilters.admin_id !== 'all' && String(log.admin_id) !== logFilters.admin_id) return false;
      if (logFilters.action  !== 'all' && log.action !== logFilters.action) return false;
      if (logFilters.level   !== 'all' && getLogLevel(log.action) !== logFilters.level) return false;
      if (logFilters.date_from && new Date(log.created_at) < new Date(logFilters.date_from)) return false;
      if (logFilters.date_to  && new Date(log.created_at) > new Date(logFilters.date_to + 'T23:59:59')) return false;
      if (logFilters.search) {
        const q = logFilters.search.toLowerCase();
        const details = typeof log.details === 'string' ? log.details.toLowerCase() : '';
        if (!(log.admin_email || '').toLowerCase().includes(q) &&
            !(log.action || '').toLowerCase().includes(q) &&
            !(log.resource || '').toLowerCase().includes(q) &&
            !details.includes(q)) return false;
      }
      return true;
    });
  }, [auditLogs, logFilters]);

  const totalPages   = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Unique actions for filter dropdown
  const uniqueActions = useMemo(() => [...new Set(auditLogs.map(l => l.action))].sort(), [auditLogs]);

  // Stats audit
  const today = new Date().toDateString();
  const criticalToday = auditLogs.filter(l => getLogLevel(l.action) === 'critical' && new Date(l.created_at).toDateString() === today).length;

  if (loading) {
    return (
      <div className="sa-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Chargement des données de sécurité...</span>
      </div>
    );
  }

  return (
    <div className="sa-container">

      {/* ── Alertes ─────────────────────────────────────────── */}
      <div className="sa-alerts-grid">
        {securityAlerts.length > 0 ? securityAlerts.map((alert, idx) => (
          <motion.div key={idx} className={`sa-alert-card sa-alert-card--${alert.type}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
            <div className="sa-alert-icon"><i className={`fas ${alert.icon}`}></i></div>
            <div className="sa-alert-content"><h4>{alert.title}</h4><p>{alert.desc}</p></div>
          </motion.div>
        )) : (
          <div className="sa-alert-card sa-alert-card--green">
            <div className="sa-alert-icon"><i className="fas fa-shield-alt"></i></div>
            <div className="sa-alert-content"><h4>Système sécurisé</h4><p>Aucune alerte de sécurité</p></div>
          </div>
        )}
      </div>

      {/* ── Stats ───────────────────────────────────────────── */}
      <div className="sa-stats-grid">
        {[
          { label: 'Administrateurs',      value: admins.length,                                     color: '#3b82f6', icon: 'fa-users-cog' },
          { label: 'Actions enregistrées', value: auditLogs.length,                                  color: '#10b981', icon: 'fa-history' },
          { label: 'Actions critiques auj.',value: criticalToday,                                    color: '#ef4444', icon: 'fa-exclamation-triangle' },
          { label: '2FA désactivée',       value: admins.filter(a => !a.two_factor_enabled).length,  color: '#f59e0b', icon: 'fa-shield-alt' },
          { label: 'Accès temporaires',    value: admins.filter(a => a.role_expires_at).length,      color: '#8b5cf6', icon: 'fa-hourglass-half' },
        ].map((s, i) => (
          <div key={i} className="sa-stat-card">
            <div className="sa-stat-header">
              <div className="sa-stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="sa-stat-icon" style={{ background: `${s.color}18`, color: s.color }}>
                <i className={`fas ${s.icon}`}></i>
              </div>
            </div>
            <div className="sa-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Table des administrateurs ────────────────────────── */}
      <div className="sa-table-card">
        <div className="sa-table-header">
          <h3><i className="fas fa-users-cog"></i> Gestion des administrateurs</h3>
          <button className="sa-btn-primary sa-btn-sm"
            onClick={() => { setNewAdminForm(EMPTY_FORM); setCreateError(''); setModal({ type: 'create' }); }}>
            <i className="fas fa-user-plus"></i> Nouvel admin
          </button>
        </div>
        <table className="sa-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Accès</th>
              <th>Dernière connexion</th>
              <th>2FA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => {
              const expiry = getExpiryInfo(admin.role_expires_at, admin.role);
              return (
                <tr key={admin.id} className={admin.role === 'super_admin' ? 'sa-table-row-highlight' : ''}>
                  <td>
                    <strong>{admin.email}</strong>
                    {admin.role === 'super_admin' && <span className="sa-badge-super">Super Admin</span>}
                  </td>
                  <td>
                    <span className={`sa-role-badge sa-role-badge--${admin.role}`}>
                      {ROLE_LABELS[admin.role] || admin.role}
                    </span>
                  </td>
                  <td>
                    <span className={`sa-status-badge ${statusClass[admin.status] || 'badge-active'}`}>
                      {statusLabel[admin.status] || 'Actif'}
                    </span>
                  </td>
                  <td>
                    {admin.role !== 'super_admin' && (
                      <span className={`sa-expiry-badge sa-expiry-${expiry.type}`}>
                        {expiry.type === 'permanent' && <i className="fas fa-infinity"></i>}
                        {expiry.type === 'expired'   && <i className="fas fa-times-circle"></i>}
                        {expiry.type === 'critical'  && <i className="fas fa-exclamation-triangle"></i>}
                        {expiry.type === 'soon'      && <i className="fas fa-hourglass-half"></i>}
                        {expiry.type === 'limited'   && <i className="fas fa-clock"></i>}
                        {' '}{expiry.label}
                      </span>
                    )}
                  </td>
                  <td className="sa-date-cell">
                    {admin.last_login ? new Date(admin.last_login).toLocaleString('fr') : 'Jamais'}
                  </td>
                  <td>
                    <span className={`sa-2fa-badge ${admin.two_factor_enabled ? 'sa-2fa-enabled' : 'sa-2fa-disabled'}`}>
                      {admin.two_factor_enabled ? 'Activé' : 'Désactivé'}
                    </span>
                  </td>
                  <td className="sa-actions">
                    <button className="sa-action-btn sa-action-btn--edit" title="Modifier rôle & accès"
                      onClick={() => {
                        setEditRole(admin.role);
                        setEditStatus(admin.status || 'active');
                        setEditIsPermanent(!admin.role_expires_at);
                        setEditExpiresAt(admin.role_expires_at ? admin.role_expires_at.slice(0, 16) : '');
                        setForce2FA(false);
                        setEditError('');
                        setModal({ type: 'edit', admin });
                      }}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="sa-action-btn sa-action-btn--history" title="Voir les actions"
                      onClick={() => setModal({ type: 'history', admin })}>
                      <i className="fas fa-history"></i>
                    </button>
                    {admin.role !== 'super_admin' && admin.status === 'active' && (
                      <button className="sa-action-btn sa-action-btn--suspend" title="Suspendre"
                        onClick={() => handleSuspendAdmin(admin.id)}>
                        <i className="fas fa-pause"></i>
                      </button>
                    )}
                    {admin.role !== 'super_admin' && admin.status === 'suspended' && (
                      <button className="sa-action-btn sa-action-btn--reactivate" title="Réactiver"
                        onClick={() => handleReactivateAdmin(admin.id)}>
                        <i className="fas fa-play"></i>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {admins.length === 0 && (
              <tr><td colSpan="7" className="sa-empty-cell">Aucun administrateur trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Rôles & Permissions ─────────────────────────────── */}
      <div className="sa-table-card">
        <div className="sa-table-header">
          <h3><i className="fas fa-lock"></i> Rôles & Permissions</h3>
          <div className="sa-perm-actions">
            <button className="sa-btn-secondary sa-btn-sm" onClick={resetPermMatrix}>
              <i className="fas fa-undo"></i> Réinitialiser
            </button>
            <button
              className={`sa-btn-primary sa-btn-sm ${permSaved ? 'sa-btn-saved' : ''}`}
              onClick={savePermMatrix}
            >
              {permSaved
                ? <><i className="fas fa-check"></i> Sauvegardé</>
                : <><i className="fas fa-save"></i> Sauvegarder</>
              }
            </button>
          </div>
        </div>
        <table className="sa-table sa-permissions-table">
          <thead>
            <tr>
              <th>Module</th>
              <th>
                <span className="sa-role-header">
                  <i className="fas fa-crown sa-role-icon sa-role-super"></i> Super Admin
                </span>
              </th>
              {ROLES_EDITABLE.map(role => (
                <th key={role}>
                  <span className="sa-role-header">
                    <i className="fas fa-user-shield sa-role-icon"></i> {ROLE_LABELS[role]}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULES.map(module => (
              <tr key={module}>
                <td><strong>{module}</strong></td>
                {/* Super Admin — toujours ON, verrouillé */}
                <td>
                  <div className="sa-toggle-wrap sa-toggle-locked">
                    <span className="sa-toggle sa-toggle--on sa-toggle--disabled">
                      <span className="sa-toggle-knob" />
                    </span>
                    <span className="sa-toggle-label sa-toggle-label--on">ON</span>
                  </div>
                </td>
                {/* Rôles éditables */}
                {ROLES_EDITABLE.map(role => {
                  const isOn = permMatrix[role]?.[module] ?? false;
                  return (
                    <td key={role}>
                      <button
                        className={`sa-toggle-wrap ${isOn ? 'sa-toggle-wrap--on' : 'sa-toggle-wrap--off'}`}
                        onClick={() => togglePerm(role, module)}
                        title={isOn ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                      >
                        <span className={`sa-toggle ${isOn ? 'sa-toggle--on' : 'sa-toggle--off'}`}>
                          <span className="sa-toggle-knob" />
                        </span>
                        <span className={`sa-toggle-label ${isOn ? 'sa-toggle-label--on' : 'sa-toggle-label--off'}`}>
                          {isOn ? 'ON' : 'OFF'}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Journal d'audit (sécurisé & filtrable) ──────────── */}
      <div className="sa-table-card">
        <div className="sa-table-header">
          <h3><i className="fas fa-shield-alt"></i> Journal d'audit sécurisé</h3>
          <div className="sa-audit-meta">
            <span className="sa-audit-total">{filteredLogs.length} entrée{filteredLogs.length !== 1 ? 's' : ''}</span>
            {criticalToday > 0 && (
              <span className="sa-audit-critical-badge">
                <i className="fas fa-exclamation-triangle"></i> {criticalToday} critique{criticalToday > 1 ? 's' : ''} aujourd'hui
              </span>
            )}
            <button className="sa-btn-secondary sa-btn-sm" onClick={fetchData}>
              <i className="fas fa-sync-alt"></i> Actualiser
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="sa-log-filters">
          <div className="sa-filter-group">
            <label><i className="fas fa-user"></i> Admin</label>
            <select className="sa-filter-select" value={logFilters.admin_id}
              onChange={e => { setLogFilters(f => ({ ...f, admin_id: e.target.value })); setCurrentPage(1); }}>
              <option value="all">Tous les admins</option>
              {admins.map(a => <option key={a.id} value={String(a.id)}>{a.email}</option>)}
            </select>
          </div>
          <div className="sa-filter-group">
            <label><i className="fas fa-tag"></i> Action</label>
            <select className="sa-filter-select" value={logFilters.action}
              onChange={e => { setLogFilters(f => ({ ...f, action: e.target.value })); setCurrentPage(1); }}>
              <option value="all">Toutes les actions</option>
              {uniqueActions.map(a => <option key={a} value={a}>{LOG_LABELS[a] || a}</option>)}
            </select>
          </div>
          <div className="sa-filter-group">
            <label><i className="fas fa-exclamation-circle"></i> Niveau</label>
            <select className="sa-filter-select" value={logFilters.level}
              onChange={e => { setLogFilters(f => ({ ...f, level: e.target.value })); setCurrentPage(1); }}>
              <option value="all">Tous les niveaux</option>
              <option value="critical">Critique</option>
              <option value="warning">Avertissement</option>
              <option value="normal">Normal</option>
            </select>
          </div>
          <div className="sa-filter-group">
            <label><i className="fas fa-calendar-alt"></i> Du</label>
            <input type="date" className="sa-filter-input" value={logFilters.date_from}
              onChange={e => { setLogFilters(f => ({ ...f, date_from: e.target.value })); setCurrentPage(1); }} />
          </div>
          <div className="sa-filter-group">
            <label><i className="fas fa-calendar-alt"></i> Au</label>
            <input type="date" className="sa-filter-input" value={logFilters.date_to}
              onChange={e => { setLogFilters(f => ({ ...f, date_to: e.target.value })); setCurrentPage(1); }} />
          </div>
          <div className="sa-filter-group sa-filter-search">
            <label><i className="fas fa-search"></i> Recherche</label>
            <input type="text" className="sa-filter-input" placeholder="Email, action, ressource..."
              value={logFilters.search}
              onChange={e => { setLogFilters(f => ({ ...f, search: e.target.value })); setCurrentPage(1); }} />
          </div>
          {(logFilters.admin_id !== 'all' || logFilters.action !== 'all' || logFilters.level !== 'all' || logFilters.date_from || logFilters.date_to || logFilters.search) && (
            <button className="sa-filter-clear" onClick={() => { setLogFilters({ admin_id: 'all', action: 'all', level: 'all', date_from: '', date_to: '', search: '' }); setCurrentPage(1); }}>
              <i className="fas fa-times"></i> Réinitialiser
            </button>
          )}
        </div>

        {/* Table logs */}
        <table className="sa-table sa-table-logs">
          <thead>
            <tr>
              <th style={{ width: 4 }}></th>
              <th>Date & heure</th>
              <th>Admin</th>
              <th>Action</th>
              <th>Ressource</th>
              <th>IP</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {paginatedLogs.map(log => {
              const level = getLogLevel(log.action);
              let details = {};
              try { details = JSON.parse(log.details || '{}'); } catch(e) {}
              const isExpanded = expandedLogId === log.id;
              return [
                <tr key={log.id} className={`sa-log-row sa-log-row--${level} ${isExpanded ? 'sa-log-row--open' : ''}`}
                  onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                  style={{ cursor: 'pointer' }}>
                  <td><div className={`sa-log-severity sa-log-severity--${level}`}></div></td>
                  <td className="sa-date-cell">
                    <span className="sa-log-date">{new Date(log.created_at).toLocaleDateString('fr')}</span>
                    <span className="sa-log-time">{new Date(log.created_at).toLocaleTimeString('fr')}</span>
                  </td>
                  <td>
                    <div className="sa-log-admin">
                      <span className="sa-log-admin-email">{log.admin_email || 'Système'}</span>
                      {log.admin_role && (
                        <span className={`sa-role-badge sa-role-badge--${log.admin_role} sa-role-badge--xs`}>
                          {ROLE_LABELS[log.admin_role] || log.admin_role}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`sa-log-badge sa-log-badge--${level}`}>
                      {level === 'critical' && <i className="fas fa-exclamation-triangle"></i>}
                      {level === 'warning'  && <i className="fas fa-exclamation-circle"></i>}
                      {level === 'normal'   && <i className="fas fa-info-circle"></i>}
                      {' '}{LOG_LABELS[log.action] || log.action}
                    </span>
                  </td>
                  <td className="sa-log-resource">{log.resource || '-'}</td>
                  <td className="sa-log-ip">
                    <i className="fas fa-map-marker-alt"></i> {log.ip_address || '-'}
                  </td>
                  <td>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} sa-log-expand-icon`}></i>
                  </td>
                </tr>,
                isExpanded && (
                  <tr key={`${log.id}-detail`} className="sa-log-detail-row">
                    <td colSpan="7">
                      <div className="sa-log-detail-panel">
                        <div className="sa-log-detail-header">
                          <span className={`sa-log-level-pill sa-log-level-pill--${level}`}>
                            {LOG_LEVEL_LABELS[level]}
                          </span>
                          <span className="sa-log-detail-id">ID #{log.id}</span>
                          <span className="sa-log-detail-timestamp">
                            {new Date(log.created_at).toLocaleString('fr', { dateStyle: 'full', timeStyle: 'medium' })}
                          </span>
                        </div>
                        <div className="sa-log-detail-grid">
                          <div className="sa-log-detail-col">
                            <p className="sa-log-detail-label">Administrateur</p>
                            <p className="sa-log-detail-value">{log.admin_email || 'Système'}</p>
                            <p className="sa-log-detail-label">Rôle au moment de l'action</p>
                            <p className="sa-log-detail-value">{ROLE_LABELS[log.admin_role] || log.admin_role || '-'}</p>
                            <p className="sa-log-detail-label">Adresse IP</p>
                            <p className="sa-log-detail-value sa-log-detail-ip">{log.ip_address || 'Non enregistrée'}</p>
                            <p className="sa-log-detail-label">Ressource affectée</p>
                            <p className="sa-log-detail-value">{log.resource || '-'}</p>
                          </div>
                          <div className="sa-log-detail-col">
                            <p className="sa-log-detail-label">Détails complets</p>
                            {Object.keys(details).length > 0 ? (
                              <div className="sa-log-json">
                                {Object.entries(details).map(([k, v]) => (
                                  <div key={k} className="sa-log-json-row">
                                    <span className="sa-log-json-key">{k}</span>
                                    <span className="sa-log-json-sep">:</span>
                                    <span className="sa-log-json-val">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="sa-log-detail-value">Aucun détail</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              ];
            })}
            {paginatedLogs.length === 0 && (
              <tr><td colSpan="7" className="sa-empty-cell">
                <i className="fas fa-search"></i> Aucune entrée correspondant aux filtres
              </td></tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="sa-pagination">
            <button className="sa-page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button className="sa-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="sa-page-info">Page {currentPage} / {totalPages}</span>
            <button className="sa-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <button className="sa-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        )}
      </div>

      {/* ── Paramètres de sécurité ───────────────────────────── */}
      <div className="sa-table-card">
        <div className="sa-table-header">
          <h3><i className="fas fa-cog"></i> Paramètres de sécurité</h3>
        </div>
        <div className="sa-settings-grid">
          <div className="sa-settings-group">
            <h4><i className="fas fa-lock"></i> Authentification</h4>
            <div className="sa-setting-item">
              <span>2FA obligatoire</span>
              <div className={`sa-toggle ${settings.twofa_required ? 'active' : ''}`} onClick={() => toggleSetting('twofa_required')}></div>
            </div>
            <div className="sa-setting-item">
              <span>Durée de session</span>
              <div className="sa-setting-input-group">
                <input type="number" className="sa-setting-input" value={settings.session_duration}
                  onChange={e => setSettings(p => ({ ...p, session_duration: +e.target.value }))} />
                <span>min</span>
              </div>
            </div>
            <div className="sa-setting-item">
              <span>Blocage après échecs</span>
              <div className="sa-setting-input-group">
                <input type="number" className="sa-setting-input" value={settings.max_attempts}
                  onChange={e => setSettings(p => ({ ...p, max_attempts: +e.target.value }))} />
                <span>tentatives</span>
              </div>
            </div>
          </div>
          <div className="sa-settings-group">
            <h4><i className="fas fa-key"></i> Politique mots de passe</h4>
            <div className="sa-setting-item">
              <span>Complexité requise</span>
              <div className={`sa-toggle ${settings.password_complexity ? 'active' : ''}`} onClick={() => toggleSetting('password_complexity')}></div>
            </div>
            <div className="sa-setting-item">
              <span>Expiration</span>
              <div className="sa-setting-input-group">
                <input type="number" className="sa-setting-input" value={settings.password_expiry}
                  onChange={e => setSettings(p => ({ ...p, password_expiry: +e.target.value }))} />
                <span>jours</span>
              </div>
            </div>
          </div>
          <div className="sa-settings-group">
            <h4><i className="fas fa-check-double"></i> Double validation</h4>
            <div className="sa-setting-item">
              <span>Remboursement &gt; 5000€</span>
              <div className={`sa-toggle ${settings.double_refund ? 'active' : ''}`} onClick={() => toggleSetting('double_refund')}></div>
            </div>
            <div className="sa-setting-item">
              <span>Modification commission</span>
              <div className={`sa-toggle ${settings.double_commission ? 'active' : ''}`} onClick={() => toggleSetting('double_commission')}></div>
            </div>
          </div>
          <div className="sa-settings-group">
            <h4><i className="fas fa-globe"></i> Restrictions IP</h4>
            <div className="sa-setting-item">
              <span>Limitation IP admin</span>
              <div className={`sa-toggle ${settings.ip_restriction ? 'active' : ''}`} onClick={() => toggleSetting('ip_restriction')}></div>
            </div>
            <div className="sa-setting-item">
              <span>IP autorisées</span>
              <button className="sa-btn-secondary sa-btn-sm">Configurer</button>
            </div>
          </div>
        </div>
        <div className="sa-settings-footer">
          <button className="sa-btn-primary" onClick={saveSettings}>
            <i className="fas fa-save"></i> Sauvegarder les paramètres
          </button>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          MODALS
      ════════════════════════════════════════════════════════ */}
      <AnimatePresence>

        {/* ── Modal : Modifier admin ──────────────────────────── */}
        {modal?.type === 'edit' && (
          <motion.div className="sa-modal-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="sa-modal sa-modal--wide"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>

              <div className="sa-modal-header">
                <h2><i className="fas fa-user-edit"></i> Modifier l'administrateur</h2>
                <button className="sa-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              <p className="sa-modal-subtitle">{modal.admin.email}</p>

              <div className="sa-modal-sections">
                {/* Colonne gauche */}
                <div className="sa-modal-col">
                  <p className="sa-modal-section-title"><i className="fas fa-user-shield"></i> Rôle & Statut</p>

                  <div className="sa-form-group">
                    <label>Rôle</label>
                    <select className="sa-form-select" value={editRole} onChange={e => setEditRole(e.target.value)}
                      disabled={modal.admin.role === 'super_admin'}>
                      <option value="super_admin">Super Admin</option>
                      <option value="admin_financier">Admin Financier</option>
                      <option value="support">Support</option>
                      <option value="analyste">Analyste</option>
                    </select>
                  </div>

                  <div className="sa-form-group">
                    <label>Statut</label>
                    <select className="sa-form-select" value={editStatus} onChange={e => setEditStatus(e.target.value)}
                      disabled={modal.admin.role === 'super_admin'}>
                      <option value="active">Actif</option>
                      <option value="suspended">Suspendu</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>

                  <label className="sa-checkbox">
                    <input type="checkbox" checked={force2FA} onChange={e => setForce2FA(e.target.checked)} />
                    <span>Forcer la réinitialisation de la 2FA</span>
                  </label>

                  {/* Permissions preview */}
                  {editRole !== 'super_admin' && PERMISSIONS[editRole] && (
                    <>
                      <p className="sa-modal-section-title" style={{ marginTop: 20 }}>
                        <i className="fas fa-table"></i> Permissions — {ROLE_LABELS[editRole]}
                      </p>
                      <table className="sa-table sa-perm-preview-table">
                        <tbody>
                          {Object.entries(PERMISSIONS[editRole]).map(([mod, perm]) => (
                            <tr key={mod}>
                              <td><strong>{mod}</strong></td>
                              <td className={PERM_CLASS[perm] || ''}>{perm}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>

                {/* Colonne droite : durée d'accès */}
                <div className="sa-modal-col">
                  <p className="sa-modal-section-title"><i className="fas fa-hourglass-half"></i> Durée d'accès</p>

                  <div className="sa-access-type-selector">
                    <div
                      className={`sa-access-type-card ${editIsPermanent ? 'sa-access-type-card--active' : ''}`}
                      onClick={() => { if (modal.admin.role !== 'super_admin') setEditIsPermanent(true); }}>
                      <div className="sa-access-type-icon" style={{ color: '#10b981' }}>
                        <i className="fas fa-infinity"></i>
                      </div>
                      <div>
                        <strong>Accès permanent</strong>
                        <p>Aucune limite de temps, révocable manuellement</p>
                      </div>
                      {editIsPermanent && <i className="fas fa-check-circle sa-access-type-check"></i>}
                    </div>

                    <div
                      className={`sa-access-type-card ${!editIsPermanent ? 'sa-access-type-card--active sa-access-type-card--timed' : ''}`}
                      onClick={() => { if (modal.admin.role !== 'super_admin') setEditIsPermanent(false); }}>
                      <div className="sa-access-type-icon" style={{ color: '#f59e0b' }}>
                        <i className="fas fa-clock"></i>
                      </div>
                      <div>
                        <strong>Accès limité</strong>
                        <p>L'accès expire automatiquement à la date choisie</p>
                      </div>
                      {!editIsPermanent && <i className="fas fa-check-circle sa-access-type-check" style={{ color: '#f59e0b' }}></i>}
                    </div>
                  </div>

                  {!editIsPermanent && (
                    <motion.div className="sa-expiry-picker" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                      <label><i className="fas fa-calendar-alt"></i> Date et heure d'expiration</label>
                      <input
                        type="datetime-local"
                        className="sa-form-input"
                        min={minDatetime()}
                        value={editExpiresAt}
                        onChange={e => setEditExpiresAt(e.target.value)}
                      />
                      {editExpiresAt && (
                        <div className="sa-expiry-preview">
                          <i className="fas fa-info-circle"></i>
                          L'accès sera révoqué automatiquement le{' '}
                          <strong>{new Date(editExpiresAt).toLocaleString('fr', { dateStyle: 'long', timeStyle: 'short' })}</strong>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {editIsPermanent && (
                    <div className="sa-expiry-permanent-note">
                      <i className="fas fa-shield-alt"></i>
                      Accès permanent jusqu'à révocation manuelle ou suspension.
                    </div>
                  )}

                  {modal.admin.role === 'super_admin' && (
                    <div className="sa-expiry-permanent-note sa-expiry-superadmin-note">
                      <i className="fas fa-crown"></i>
                      Le Super Admin a toujours un accès permanent et ne peut pas être limité.
                    </div>
                  )}
                </div>
              </div>

              {editError && <div className="sa-create-error"><i className="fas fa-exclamation-circle"></i> {editError}</div>}

              <div className="sa-modal-actions">
                <button className="sa-btn-primary" onClick={handleSaveAdmin} disabled={saving || modal.admin.role === 'super_admin'}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Sauvegarde...</> : <><i className="fas fa-save"></i> Enregistrer</>}
                </button>
                <button className="sa-btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* ── Modal : Nouvel admin ────────────────────────────── */}
        {modal?.type === 'create' && (
          <motion.div className="sa-modal-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="sa-modal sa-modal--wide"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>

              <div className="sa-modal-header">
                <h2><i className="fas fa-user-plus"></i> Nouvel administrateur</h2>
                <button className="sa-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>

              <div className="sa-modal-sections">
                <div className="sa-modal-col">
                  <p className="sa-modal-section-title"><i className="fas fa-lock"></i> Accès</p>

                  <div className="sa-form-group">
                    <label>Email</label>
                    <input type="email" className="sa-form-input" placeholder="admin@exemple.com"
                      value={newAdminForm.email} onChange={e => setNewAdminForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="sa-form-group">
                    <label>Mot de passe</label>
                    <input type="password" className="sa-form-input" placeholder="Min. 8 caractères"
                      value={newAdminForm.password} onChange={e => setNewAdminForm(f => ({ ...f, password: e.target.value }))} />
                  </div>
                  <div className="sa-form-group">
                    <label>Confirmer le mot de passe</label>
                    <input type="password" className="sa-form-input" placeholder="Répéter le mot de passe"
                      value={newAdminForm.confirmPassword} onChange={e => setNewAdminForm(f => ({ ...f, confirmPassword: e.target.value }))} />
                  </div>

                  <p className="sa-modal-section-title" style={{ marginTop: 20 }}><i className="fas fa-user-shield"></i> Rôle</p>

                  <div className="sa-role-selector">
                    {[
                      { value: 'admin_financier', label: 'Admin Financier', icon: 'fa-coins',     desc: 'Packs, transactions, remboursements' },
                      { value: 'support',         label: 'Support',         icon: 'fa-headset',   desc: 'Lecture transactions uniquement' },
                      { value: 'analyste',        label: 'Analyste',        icon: 'fa-chart-bar', desc: 'Lecture seule tous modules' },
                    ].map(r => (
                      <div key={r.value}
                        className={`sa-role-option ${newAdminForm.role === r.value ? 'sa-role-option--active' : ''}`}
                        onClick={() => setNewAdminForm(f => ({ ...f, role: r.value }))}>
                        <div className="sa-role-option-icon"><i className={`fas ${r.icon}`}></i></div>
                        <div><strong>{r.label}</strong><p>{r.desc}</p></div>
                        {newAdminForm.role === r.value && <i className="fas fa-check-circle sa-role-check"></i>}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sa-modal-col">
                  <p className="sa-modal-section-title"><i className="fas fa-table"></i> Permissions — {ROLE_LABELS[newAdminForm.role]}</p>
                  <table className="sa-table sa-perm-preview-table">
                    <thead><tr><th>Module</th><th>Accès</th></tr></thead>
                    <tbody>
                      {Object.entries(PERMISSIONS[newAdminForm.role] || {}).map(([mod, perm]) => (
                        <tr key={mod}>
                          <td><strong>{mod}</strong></td>
                          <td className={PERM_CLASS[perm] || ''}>{perm}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {createError && <div className="sa-create-error"><i className="fas fa-exclamation-circle"></i> {createError}</div>}

              <div className="sa-modal-actions">
                <button className="sa-btn-primary" onClick={handleCreateAdmin} disabled={saving}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Création...</> : <><i className="fas fa-user-plus"></i> Créer l'administrateur</>}
                </button>
                <button className="sa-btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* ── Modal : Historique d'un admin ───────────────────── */}
        {modal?.type === 'history' && (
          <motion.div className="sa-modal-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="sa-modal sa-modal--history"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>

              <div className="sa-modal-header">
                <h2><i className="fas fa-user-clock"></i> Historique — {modal.admin.email}</h2>
                <button className="sa-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>

              <div className="sa-history-meta">
                <span className={`sa-role-badge sa-role-badge--${modal.admin.role}`}>{ROLE_LABELS[modal.admin.role]}</span>
                <span className={`sa-status-badge ${statusClass[modal.admin.status] || 'badge-active'}`}>
                  {statusLabel[modal.admin.status]}
                </span>
                {modal.admin.role !== 'super_admin' && (() => {
                  const e = getExpiryInfo(modal.admin.role_expires_at, modal.admin.role);
                  return <span className={`sa-expiry-badge sa-expiry-${e.type}`}>{e.label}</span>;
                })()}
              </div>

              <div className="sa-history-table-wrap">
                <table className="sa-table sa-table-logs">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Date & heure</th>
                      <th>Action</th>
                      <th>Ressource</th>
                      <th>IP</th>
                      <th>Détails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.filter(l => String(l.admin_id) === String(modal.admin.id)).map(log => {
                      const level = getLogLevel(log.action);
                      let details = {};
                      try { details = JSON.parse(log.details || '{}'); } catch(e) {}
                      return (
                        <tr key={log.id} className={`sa-log-row sa-log-row--${level}`}>
                          <td><div className={`sa-log-severity sa-log-severity--${level}`}></div></td>
                          <td className="sa-date-cell">
                            <span className="sa-log-date">{new Date(log.created_at).toLocaleDateString('fr')}</span>
                            <span className="sa-log-time">{new Date(log.created_at).toLocaleTimeString('fr')}</span>
                          </td>
                          <td>
                            <span className={`sa-log-badge sa-log-badge--${level}`}>
                              {LOG_LABELS[log.action] || log.action}
                            </span>
                          </td>
                          <td>{log.resource || '-'}</td>
                          <td className="sa-log-ip">{log.ip_address || '-'}</td>
                          <td className="sa-log-details">
                            {Object.entries(details).slice(0, 3).map(([k, v]) => (
                              <span key={k} className="sa-detail-chip"><strong>{k}:</strong> {String(v)}</span>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                    {auditLogs.filter(l => String(l.admin_id) === String(modal.admin.id)).length === 0 && (
                      <tr><td colSpan="6" className="sa-empty-cell">
                        <i className="fas fa-history"></i> Aucune action enregistrée pour cet administrateur
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="sa-modal-actions">
                <button className="sa-btn-secondary" onClick={() => setModal(null)}>Fermer</button>
              </div>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default SecuriteAdmin;
